// app/components/ScriptEditor.tsx
'use client'

import React, { useState, useEffect, useRef } from 'react'
import Editor from '@monaco-editor/react'
import { createVisualInteractionUtils, AnimationStatus } from '../../AITerminal/utils/visualInteractions'

interface ScriptEditorProps {
  // Called when the user clicks "Apply"
  onApply: (scriptCode: string) => void
}

export default function ScriptEditor({ onApply }: ScriptEditorProps) {
  const [code, setCode] = useState(`
// Example user script
function main(data) {
  // data: array of candles = [{time, open, high, low, close, volume}, ...]
  // Return array of { time, value } to plot

  // For instance, a naive 14-bar SMA:
  let period = 14
  let results = []
  let sum = 0
  for (let i = 0; i < data.length; i++) {
    sum += data[i].close
    if (i >= period) {
      sum -= data[i - period].close
    }
    if (i >= period - 1) {
      results.push({
        time: data[i].time,
        value: sum / period
      })
    } else {
      results.push({
        time: data[i].time,
        value: NaN
      })
    }
  }
  return results
}
  `)

  // Refs and state for visual interactions
  const scriptEditorContainerRef = useRef<HTMLDivElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [lastAction, setLastAction] = useState('');
  const visualUtils = createVisualInteractionUtils();

  const handleEditorChange = (value: string | undefined) => {
    if (value !== undefined) {
      setCode(value)
    }
  }

  const handleApply = () => {
    onApply(code)
  }

  // Effect to handle AI-triggered script application
  useEffect(() => {
    const handleAIScriptApply = async (event: Event) => { 
      const customEvent = event as CustomEvent; // Cast to CustomEvent
      const { scriptName, scriptContent } = customEvent.detail;
      console.log('ScriptEditor: Received AI script to apply:', { scriptName, scriptContent });

      setIsAnimating(true);
      setLastAction(`⚙️ Applying script: "${scriptName || 'Untitled Script'}"...`);

      try {
        if (scriptEditorContainerRef.current) {
          await visualUtils.scrollIntoView(scriptEditorContainerRef as React.RefObject<HTMLElement>);
          await visualUtils.highlightElement(scriptEditorContainerRef as React.RefObject<HTMLElement>, 1200);
        }

        if (typeof scriptContent === 'string') {
          setCode(scriptContent);
          onApply(scriptContent);

          // Dispatch success event
          window.dispatchEvent(new CustomEvent('applyScriptToEditorResult', {
            detail: {
              success: true,
              scriptName: scriptName || 'Untitled Script',
              message: `Script "${scriptName || 'Untitled Script'}" applied and executed.`,
              timestamp: new Date(),
            }
          }));
          setLastAction(`✅ Script "${scriptName || 'Untitled Script'}" applied!`);
        } else {
          throw new Error('scriptContent is missing or not a string.');
        }
      } catch (error: any) {
        console.error('ScriptEditor: Error applying AI script:', error);
        // Dispatch error event
        window.dispatchEvent(new CustomEvent('applyScriptToEditorResult', {
          detail: {
            success: false,
            scriptName: scriptName || 'Untitled Script',
            error: error.message || 'Failed to apply script due to an internal error.',
            timestamp: new Date(),
          }
        }));
        setLastAction(`❌ Error applying script: ${error.message}`);
      } finally {
        setTimeout(() => {
          setIsAnimating(false);
          setLastAction('');
        }, 2500);
      }
    };

    // Cast the async handler to unknown then to EventListener to satisfy TypeScript
    window.addEventListener('applyScriptToEditor', handleAIScriptApply as unknown as EventListener);
    return () => {
      window.removeEventListener('applyScriptToEditor', handleAIScriptApply as unknown as EventListener);
    };
  }, [onApply, visualUtils]); // Added visualUtils to dependencies

  const [editorTheme, setEditorTheme] = useState('vs-dark')

  // Listen for theme changes
  useEffect(() => {
    const handleThemeChange = (e: CustomEvent<{ isDark: boolean }>) => {
      setEditorTheme(e.detail.isDark ? 'vs-dark' : 'vs-light')
    }

    // Set initial theme
    setEditorTheme(
      document.documentElement.classList.contains('dark')
        ? 'vs-dark'
        : 'vs-light'
    )

    window.addEventListener('themeChange', handleThemeChange as EventListener)
    return () => {
      window.removeEventListener(
        'themeChange',
        handleThemeChange as EventListener
      )
    }
  }, [])

  return (
    <div ref={scriptEditorContainerRef} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <AnimationStatus isAnimating={isAnimating} lastAction={lastAction} />
      <Editor
        value={code}
        language="javascript"
        height="calc(100% - 40px)"
        theme={editorTheme}
        onChange={handleEditorChange}
      />
      <button onClick={handleApply} style={{ height: 40, marginTop: 4 }}>
        Apply
      </button>
    </div>
  )
}
